import React from "react";
import { Box, Typography } from "@mui/material";
import { DotLegalHeader, DotLegalLink, DotLegalTextField, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { annualWheelActivityIcon } from "@common/icons";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../useUrlProvider";
import { useTaskPaneHeaderStyles } from "../taskPane/taskPaneHeader/TaskPaneHeader.styles";

export interface ITaskPdfHeaderProps {
    activityName: string;
    taskName?: string;
    processingActivtiyId: string | null;
    annualWheelActivityId?: string;
    customTaskId: string | undefined | null;
    isAdHocTask?: boolean;
    riskMitigationPlanId?: string | null;
}

/**
 * The printed task's title block. What the pane's header shows, minus everything there is no one to press
 * on a page: the controls, the overflow menu and the activity navigation.
 */
function TaskPdfHeader(props: ITaskPdfHeaderProps) {
    const { translateString } = useTranslation();
    const styles = useTaskPaneHeaderStyles();
    const { permissions } = useUserContext();
    const { getAnnualWheelUrlWithActivityId, getProcessingActivityValidationUrl } = useUrlProvider();

    // A risk mitigation task's title is owned by its plan (name + scenario), so it is locked and gets a fixed,
    // translated "Risk Mitigation:" label, mirroring how a processing activity validation task is presented.
    const isRiskMitigation = Boolean(props.riskMitigationPlanId);
    const displayName = isRiskMitigation ? `${translateString("riskMitigationTaskLabel")}: ${props.activityName}` : props.activityName;

    return (
        <Box sx={{ position: "relative", fontSize: 14, fontWeight: 600, textTransform: "uppercase", width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", paddingBottom: 1, fontSize: "18px !important" }}>
                <Box sx={styles.titleBlock}>
                    {props.isAdHocTask && permissions.taskPermissions.edit && !isRiskMitigation ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            {props.customTaskId && (
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    {props.customTaskId}
                                </DotLegalHeader>
                            )}
                            <Box
                                sx={(theme) => ({
                                    "& .MuiInputBase-input": {
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        color: theme.palette.primary.dark,
                                    },
                                    flex: 1,
                                    marginTop: "-0.7px",
                                })}
                            >
                                <DotLegalTextField disabled noMargin variant="standard" label={""} value={props.activityName ?? ""} debounce />
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={styles.headerContainer}>
                            {props.customTaskId && (
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    {props.customTaskId}
                                </DotLegalHeader>
                            )}
                            <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                {displayName}
                            </DotLegalHeader>
                            {props.taskName && (
                                <React.Fragment>
                                    <Typography sx={styles.seperator}>/</Typography>
                                    <DotLegalHeader marginBottom={0} color="secondary" headerStyle={"small"}>
                                        {props.processingActivtiyId !== null ? (
                                            <DotLegalLink to={getProcessingActivityValidationUrl(props.processingActivtiyId)}>{props.taskName}</DotLegalLink>
                                        ) : (
                                            <span>{props.taskName}</span>
                                        )}
                                    </DotLegalHeader>
                                </React.Fragment>
                            )}
                            {!props.isAdHocTask && (
                                <Box sx={{ "& svg": { width: 20, height: 20 } }}>
                                    <DotLegalLink to={getAnnualWheelUrlWithActivityId(props.annualWheelActivityId!)}>
                                        <DotLegalTooltip text={translateString("connectedToAnnualWheelActivity")}>{annualWheelActivityIcon}</DotLegalTooltip>
                                    </DotLegalLink>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default TaskPdfHeader;
